import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, useMediaQuery, useTheme, Typography, Container, Fade, Paper, Grid } from '@mui/material';
import {
  CharacterPageLoader,
  Sidebar,
  MobileNav,
  useCharacterData,
  CharacterProfile,
  CharacterSection,
  AuthenticationModal,
} from '@client/components';
import { CreateNewChat } from '@client/components/service';

export function CharacterPage() {
  const { char_uuid } = useParams<{ char_uuid: string }>();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState({
    startChat: false,
    editCharacter: false,
    deleteCharacter: false,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { character, recommendedCharacters, loading, userUuid, isLoggedIn, handleDeleteCharacter, handleReportCharacter } =
    useCharacterData(char_uuid!);

  if (loading) return <CharacterPageLoader />;

  if (!character) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          404 - Character Not Found
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
          The character you are looking for does not exist or you do not have
          permission to view it.
        </Typography>
      </Box>
    );
  }

  const handleStartChat = async () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    setIsLoading((prev) => ({ ...prev, startChat: true }));
    try {
      const result = await CreateNewChat(character.char_uuid, userUuid!);
      if (result?.chat_uuid) navigate(`/chat/${result.chat_uuid}`);
      else console.error('Chat creation failed:', result);
    } catch (err) {
      console.error('Error starting chat:', err);
    } finally {
      setIsLoading((prev) => ({ ...prev, startChat: false }));
    }
  };

  const handleCharacterDeletion = async () => {
    setIsLoading((prev) => ({ ...prev, deleteCharacter: true }));
    const success = await handleDeleteCharacter();
    if (success) navigate('/Home');
    setIsLoading((prev) => ({ ...prev, deleteCharacter: false }));
  };

  const handleCreatorClick = () => {
    if (character.creator_uuid)
      navigate(`/profile/${character.creator_uuid}`);
  };

  const handleEditCharacter = () => {
    setIsLoading((prev) => ({ ...prev, editCharacter: true }));
    navigate(`/create/${character.char_uuid}`);
    setIsLoading((prev) => ({ ...prev, editCharacter: false }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
        display: 'flex',
      }}
    >
      <Sidebar />
      {isMobile && <MobileNav setShowLoginModal={setShowLoginModal} />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: '72px' },
          minHeight: '100vh',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Fade in timeout={600}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 6 }}>
                <CharacterProfile
                  character={character}
                  isLoading={isLoading}
                  creatorUuid={userUuid}
                  isLoggedIn={isLoggedIn}
                  handleStartChat={handleStartChat}
                  handleEditCharacter={handleEditCharacter}
                  handleCharacterDeletion={handleCharacterDeletion}
                  handleCreatorClick={handleCreatorClick}
                  handleReportCharacter={(reportText, creatorUuid) =>
                    handleReportCharacter(reportText, creatorUuid)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <CharacterSection character={character} />
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </Box>

      {showLoginModal && (
        <AuthenticationModal
          mode={authMode}
          onClose={() => setShowLoginModal(false)}
          toggleMode={() =>
            setAuthMode((prev) => (prev === 'login' ? 'register' : 'login'))
          }
        />
      )}
    </Box>
  );
}